import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { FIELD_NAMES } from '../../BasicSettings';
import { getEnabledStudentIds } from 'chameleon/legacy/src/components/assessment/AssignItem/components/AssignModal/Form/ClassesStudentsSelectDropdown/utils';

/**
 * When we have CAS setting present, it is important for the teacher to know
 * which setting the student is in, if any.
 *
 * This utility function checks the existing class dropdown form data for each
 * setting to allow us append (Setting X) to the student name where
 * X is the group where the student is in.
 *
 * (Setting 1) would tell the teacher the student is a part of the main assignment
 * settings
 *
 * (Setting 5) would tell the teacher they can find this partocular student
 * selected in the "Assignment Setting 5" group.
 *
 * An empty postfix tells the teacher the student is not yet selected anywhere.
 */
export const getStudentsAssignmentSettingPostfix = (
  sectionId,
  studentId,
  otherActiveSettings,
  getValues,
) => {
  const studentsSettings = (otherActiveSettings || []).filter(
    ({ id: settingId }) => {
      const selectedSections = getValues(
        `${settingId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
      );
      const selectedStudents = get(selectedSections, sectionId, []);
      if (!get(selectedStudents, 'student_ids', false)) return false;

      return selectedStudents.student_ids.includes(studentId);
    },
  );

  if (!studentsSettings.length) return '';

  return ` (Setting ${get(studentsSettings, '[0].settingNumber', 0)})`;
};

/**
 * Retrieves the active class selection from the assign modal.
 *
 * @param {Object} cls - The current class object.
 * @param {Array} otherActiveSettings - Other settings where we can get the previously selected class(es).
 * @param {Function} getValues - React-hook-form method to get those classes and students dropdown settings.
 * @returns {Object} An object where it is telling us if class ID is selected elsewhere and where is an object containing:
 *                   - isFullClassSelectedElsewhere: {boolean} - True if all students are selected, false otherwise.
 *                   - settingNumber: {number} - The setting number associated with the class.
 */
export const getActiveClassSelection = (
  cls,
  otherActiveSettings,
  disabledStudents,
  getValues,
) => {
  let response = {};

  (otherActiveSettings || []).map(({ id: settingId, settingNumber }) => {
    const selectedSections = getValues(
      `${settingId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
    );

    /**
     * Only keep checking if we haven't found that the full section
     * is selected elsewhere
     */
    if (!isEmpty(response)) return;

    /**
     * For an empty class, it's enough to check if the key exists in
     * the other setting
     */
    if (cls.students.length === 0) {
      /**
       * Let's only update the dict if we are sure this
       * full-class is selected elsewhere.
       */
      if (!(cls.id in selectedSections)) return;

      response = {
        isFullClassSelectedElsewhere: cls.id in selectedSections,
        settingNumber,
      };
      return;
    }

    const selectedStudentIds = selectedSections[cls.id]?.student_ids || [];
    const selectableStudentIds = getEnabledStudentIds(cls, disabledStudents);
    const allStudentIds = cls.students
      .filter((student) => selectableStudentIds.includes(String(student.id)))
      .map((student) => student.id);

    const isFullClassSelectedElsewhere = allStudentIds.every((studentId) =>
      selectedStudentIds.includes(studentId),
    );

    /**
     * Don't bother updating the dict, since leaving it empty implies
     * the section is not selected elsewhere.
     */
    if (!isFullClassSelectedElsewhere) return;

    response = {
      isFullClassSelectedElsewhere,
      settingNumber,
    };
  });

  return response;
};
